import { CommonModule } from '@angular/common';
import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { GenVideoPreviewData } from '../../../../../../../models/google-template/preview-interfaces/gen-video-preview-data.interface';
import { VideoFormat, YoutubeFormatHelper } from '../../../../../../../utils/youtube-format-helper';
import { TvFrameComponent } from '../../../preview-common/tv-frame/tv-frame.component';

/**
 * In-feed video ad on the TV home screen: the creative is the first card of the
 * "Recommended" shelf, drawn as the focused one, with its copy underneath and
 * the rest of the feed carrying on past the screen edge.
 *
 * Unlike the Shorts placements this set draws, nothing is overlaid on the
 * picture — the card is an ordinary feed tile and the ad reads as one.
 */
@Component({
    selector: 'app-gen-youtube-home-feed-ad-tv',
    imports: [CommonModule, TvFrameComponent],
    templateUrl: './youtube-home-feed-ad-tv.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './youtube-home-feed-ad-tv.component.scss'
})
export class YoutubeHomeFeedAdTvPreviewComponent {
  @Input() data!: GenVideoPreviewData;

  /** The creative format this placement previews, found by measured ratio. */
  readonly format: VideoFormat = '16:9';

  /** The organic cards drawn beside and below the ad. Count, not content. */
  readonly fillersBesideAd = [1, 2, 3];
  readonly fillersBelowAd = [1, 2, 3, 4];

  /** The TV app's side rail, as bootstrap-icon names. Fixed furniture. */
  readonly railIcons = [
    'search',
    'house-door-fill',
    'play-btn-fill',
    'collection-play-fill',
    'camera-video-fill',
    'bag-fill',
    'display-fill',
    'list',
  ];
  readonly railIconsBottom = ['gear-fill', 'box-arrow-in-right'];

  /** Thumbnail of the 16:9 video, or '' when there isn't one yet. */
  get thumbnailUrl(): string {
    return YoutubeFormatHelper.thumbnailForFormat(this.videoIds, this.format);
  }

  /**
   * True while any link is still being measured, so the empty card says so
   * rather than asking for a video that is about to turn up.
   */
  get isResolving(): boolean {
    return YoutubeFormatHelper.isResolvingAny(this.videoIds);
  }

  /**
   * The card gives the title two lines, so it takes the long headline and only
   * falls back to the short one when there isn't one.
   */
  get headline(): string {
    return this.data.longHeadlines[0] || this.data.headlines[0] || 'Your headline appears here';
  }

  private get videoIds(): string[] {
    return this.data.videos.map((video) => video.videoId);
  }
}
